<template>
  <!-- Phones only: once the hero's own buttons have scrolled away, keep "Start free trial"
       in thumb reach. Hidden again over the pinned details panel (it would cover the answer),
       the pricing section, the closing CTA and the footer, which carry their own buttons, and
       while the cookie banner needs an answer. Fixed
       position, so it never changes the page height. -->
  <div class="home-sticky-cta" :class="{ on: visible }" :aria-hidden="!visible">
    <NuxtLink
      class="hsc-secondary"
      :to="localePath('/pricing')"
      :tabindex="visible ? 0 : -1"
      data-conversion-event="pricing_click"
      data-conversion-label="Home sticky CTA pricing"
      data-conversion-location="home-sticky-cta"
    >
      {{ localeUi.nav.pricing }}
    </NuxtLink>
    <a
      class="hsc-primary"
      :href="addReferralToUrl('https://app.workspace369.com/signup')"
      :tabindex="visible ? 0 : -1"
      data-conversion-event="start_free_click"
      data-conversion-label="Home sticky CTA"
      data-conversion-location="home-sticky-cta"
    >
      {{ homeCopy.hero.primaryCta }}
    </a>
    <TrialDisclosure compact centered class="hsc-note" />
  </div>
</template>

<script setup>
const { addReferralToUrl } = useReferral();
const { localeUi, localePath } = useWorkspaceLocale();
const homeCopy = useHomeCopy();
const { needsBanner } = useCookieConsent();

const pastHero = ref(false);
const overOwnCta = ref(false);
const visible = computed(() => pastHero.value && !overOwnCta.value && !needsBanner.value);

let heroObserver = null;
let ctaObserver = null;
const showing = new Set();

onMounted(() => {
  // The hero's own button row. Not the first trial link in the hero: that is the referral strip's,
  // hidden (zero box, top 0) for most visitors, which kept the bar off on every phone.
  const heroButtons = document.querySelector(".hero-section .hero-actions");
  if (heroButtons) {
    heroObserver = new IntersectionObserver(([entry]) => {
      // Past the hero once its button has left the top of the screen.
      pastHero.value = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    });
    heroObserver.observe(heroButtons);
  }
  ctaObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) showing.add(entry.target);
      else showing.delete(entry.target);
    }
    overOwnCta.value = showing.size > 0;
  });
  document.querySelectorAll("#home-pricing, [data-sticky-cta-hide], footer").forEach((el) => ctaObserver.observe(el));
});

onBeforeUnmount(() => {
  heroObserver?.disconnect();
  ctaObserver?.disconnect();
});
</script>

<style scoped>
.home-sticky-cta {
  display: none;
}

@media (max-width: 767px) {
  .home-sticky-cta {
    position: fixed;
    z-index: 40;
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.6rem;
    border-top: 1px solid rgb(15 23 42 / 0.08);
    background: rgb(255 255 255 / 0.92);
    box-shadow: 0 -12px 30px rgb(15 23 42 / 0.1);
    padding: 0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom));
    pointer-events: none;
    transform: translateY(110%);
    transition: transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1);
    -webkit-backdrop-filter: blur(14px);
    backdrop-filter: blur(14px);
  }

  .dark .home-sticky-cta {
    border-color: rgb(148 163 184 / 0.16);
    background: rgb(2 6 23 / 0.9);
    box-shadow: 0 -12px 30px rgb(0 0 0 / 0.4);
  }

  .home-sticky-cta.on {
    pointer-events: auto;
    transform: none;
  }

  /* TrialDisclosure has two root nodes, so this scoped class never reached it and the line took the
     narrow first column, widening it to the text and pushing the trial button off the screen. */
  .home-sticky-cta :deep(.hsc-note) {
    grid-column: 1 / -1;
    margin: 0;
  }

  .hsc-secondary,
  .hsc-primary {
    display: inline-flex;
    min-height: 2.9rem;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-size: 0.92rem;
    font-weight: 600;
    padding: 0 1.1rem;
  }

  .hsc-secondary {
    border: 1px solid rgb(203 213 225);
    color: #0f172a;
  }

  .dark .hsc-secondary {
    border-color: rgb(51 65 85);
    color: #e2e8f0;
  }

  .hsc-primary {
    background: #0f172a;
    color: #fff;
  }

  .dark .hsc-primary {
    background: #f8fafc;
    color: #0f172a;
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-sticky-cta {
    transition: none;
  }
}
</style>
